/**
 * Trovva — CRM Tasks Tab
 *
 * Standalone task management within the CRM horizontal nav.
 * Shows all tasks for the org (broker) or the agent's own tasks.
 * Supports filtering by range (today, this week, overdue, all),
 * creating new tasks, marking complete, and linking to contacts.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  CheckCircle2, Circle, Plus, Clock, AlertTriangle, Calendar,
  Loader2, User, ChevronDown, X, Filter, Search,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';
import { formatDateRelative } from '../../lib/dateFormat';
import AgentFilterCombo from '../common/AgentFilterCombo';

const RANGES = [
  { id: 'today', label: 'Today' },
  { id: 'this_week', label: 'This Week' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'all', label: 'All' },
];

const PRIORITIES = [
  { id: 'high', label: 'High', color: '#EF4444' },
  { id: 'medium', label: 'Medium', color: '#F59E0B' },
  { id: 'low', label: 'Low', color: '#6B7280' },
];

export default function TasksTab({ demoMode, showToast, agents = [], onOpenLead }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('today');
  const [agentFilter, setAgentFilter] = useState('mine');
  const [showAdd, setShowAdd] = useState(false);

  // Add task form state
  const [newTitle, setNewTitle] = useState('');
  const [newDue, setNewDue] = useState(() => new Date().toISOString().split('T')[0]);
  const [newPriority, setNewPriority] = useState('medium');
  const [newDescription, setNewDescription] = useState('');
  const [newAssignee, setNewAssignee] = useState(null); // { id, name } or null = self
  const [assigneeQuery, setAssigneeQuery] = useState('');
  const [showAssigneeMenu, setShowAssigneeMenu] = useState(false);
  const [creating, setCreating] = useState(false);
  const assigneeRef = useRef(null);

  // Close assignee dropdown on outside click
  useEffect(() => {
    if (!showAssigneeMenu) return;
    const handler = (e) => {
      if (assigneeRef.current && !assigneeRef.current.contains(e.target)) {
        setShowAssigneeMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAssigneeMenu]);

  const loadTasks = useCallback(async () => {
    if (demoMode) {
      setTasks(DEMO_TASKS);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      let url = `/api/tasks?range=${range}`;
      // 'mine' = current user's own tasks, 'all' = entire org, UUID = specific agent
      if (agentFilter === 'mine') {
        url += '&agent_id=me';
      } else if (agentFilter !== 'all') {
        // specific agent UUID
        url += `&agent_id=${agentFilter}`;
      }
      // 'all' = no agent_id param → broker sees entire org
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to load tasks');
      setTasks(d.tasks || []);
    } catch (e) { showToast?.(e.message); }
    finally { setLoading(false); }
  }, [range, agentFilter, demoMode, showToast]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const toggleComplete = async (task) => {
    if (demoMode) return;
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/tasks?id=${task.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: newStatus,
          completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
        }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      showToast?.(newStatus === 'completed' ? 'Task completed' : 'Task reopened');
      loadTasks();
    } catch (e) { showToast?.(e.message); }
  };

  const createTask = async () => {
    if (!newTitle.trim()) { showToast?.('Title is required'); return; }
    if (demoMode) { setShowAdd(false); return; }
    setCreating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/tasks', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newTitle.trim(),
          due_date: newDue,
          priority: newPriority,
          description: newDescription.trim() || undefined,
          agent_id: newAssignee?.id || undefined,
        }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      showToast?.(newAssignee ? `Task assigned to ${newAssignee.name}` : 'Task created');
      setNewTitle(''); setNewDescription(''); setNewPriority('medium');
      setNewDue(new Date().toISOString().split('T')[0]);
      setNewAssignee(null); setAssigneeQuery('');
      setShowAdd(false);
      loadTasks();
    } catch (e) { showToast?.(e.message); }
    finally { setCreating(false); }
  };

  const isOverdue = (task) => {
    if (task.status === 'completed') return false;
    return task.due_date < new Date().toISOString().split('T')[0];
  };

  const priorityMeta = (p) => PRIORITIES.find(x => x.id === p) || PRIORITIES[1];

  // Group tasks: overdue first, then by date
  const pending = tasks.filter(t => t.status !== 'completed');
  const completed = tasks.filter(t => t.status === 'completed');
  const overdue = pending.filter(t => isOverdue(t));
  const upcoming = pending.filter(t => !isOverdue(t));

  return (
    <div style={{ minHeight: 300 }}>
      {/* ── Header row ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 12, gap: 12, flexWrap: 'wrap',
      }}>
        {/* Range filter pills */}
        <div data-tour="task-ranges" style={{ display: 'flex', gap: 4 }}>
          {RANGES.map(r => (
            <button key={r.id} onClick={() => setRange(r.id)} style={{
              padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: range === r.id ? 700 : 500,
              background: range === r.id ? theme.colors.primary : `${theme.colors.primary}10`,
              color: range === r.id ? 'white' : theme.colors.text,
              transition: 'all 0.15s',
            }}>{r.label}</button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Agent filter — only visible to brokers who have agents */}
          {agents.length > 0 && (
            <AgentFilterCombo
              agents={agents}
              value={agentFilter}
              onChange={setAgentFilter}
              mineLabel="My Tasks"
              allLabel="All Agents"
              dataTour="task-agent-filter"
            />
          )}

        <button data-tour="task-add" onClick={() => setShowAdd(!showAdd)} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
          background: theme.colors.primary, color: 'white',
          fontSize: 12, fontWeight: 600,
        }}>
          <Plus size={14} /> Add Task
        </button>
        </div>
      </div>

      {/* ── Add task form ── */}
      {showAdd && (
        <div style={{
          padding: 16, borderRadius: 12, marginBottom: 16,
          background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <input
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              placeholder="Task title..."
              style={{
                flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 8,
                border: `1px solid ${theme.colors.border}`, fontSize: 13,
                background: theme.colors.bg, color: theme.colors.text,
                outline: 'none',
              }}
              onKeyDown={e => { if (e.key === 'Enter') createTask(); }}
            />
            <input
              type="date"
              value={newDue}
              onChange={e => setNewDue(e.target.value)}
              style={{
                padding: '8px 12px', borderRadius: 8,
                border: `1px solid ${theme.colors.border}`, fontSize: 13,
                background: theme.colors.bg, color: theme.colors.text,
              }}
            />
            <select
              value={newPriority}
              onChange={e => setNewPriority(e.target.value)}
              style={{
                padding: '8px 12px', borderRadius: 8,
                border: `1px solid ${theme.colors.border}`, fontSize: 13,
                background: theme.colors.bg, color: theme.colors.text,
              }}
            >
              {PRIORITIES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <textarea
            value={newDescription}
            onChange={e => setNewDescription(e.target.value)}
            placeholder="Description (optional)..."
            rows={2}
            style={{
              width: '100%', padding: '8px 12px', borderRadius: 8, resize: 'vertical',
              border: `1px solid ${theme.colors.border}`, fontSize: 13,
              background: theme.colors.bg, color: theme.colors.text,
              outline: 'none', marginBottom: 12, boxSizing: 'border-box',
            }}
          />

          {/* ── Assign to agent (fuzzy combobox) ── */}
          {agents.length > 0 && (
            <div ref={assigneeRef} style={{ position: 'relative', marginBottom: 12 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', borderRadius: 8,
                border: `1px solid ${showAssigneeMenu ? theme.colors.primary : theme.colors.border}`,
                background: theme.colors.bg, transition: 'border-color 0.15s',
              }}>
                <User size={14} style={{ color: theme.colors.textLight, flexShrink: 0 }} />
                <input
                  value={newAssignee ? newAssignee.name : assigneeQuery}
                  onChange={e => {
                    setAssigneeQuery(e.target.value);
                    setNewAssignee(null);
                    setShowAssigneeMenu(true);
                  }}
                  onFocus={() => setShowAssigneeMenu(true)}
                  placeholder="Assign to agent (type to search)..."
                  style={{
                    flex: 1, border: 'none', outline: 'none', fontSize: 13,
                    background: 'transparent', color: theme.colors.text,
                  }}
                />
                {newAssignee && (
                  <button onClick={() => { setNewAssignee(null); setAssigneeQuery(''); }} style={{
                    background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                    color: theme.colors.textLight, display: 'flex',
                  }}>
                    <X size={14} />
                  </button>
                )}
                <ChevronDown size={14} style={{
                  color: theme.colors.textLight, flexShrink: 0, cursor: 'pointer',
                  transform: showAssigneeMenu ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.15s',
                }} onClick={() => setShowAssigneeMenu(!showAssigneeMenu)} />
              </div>

              {/* Dropdown menu */}
              {showAssigneeMenu && (() => {
                const q = assigneeQuery.toLowerCase().trim();
                const filtered = agents.filter(a => {
                  const name = `${a.first_name || ''} ${a.last_name || ''} ${a.email || ''}`.toLowerCase();
                  if (!q) return true;
                  // Fuzzy: every word in query must appear somewhere in name
                  return q.split(/\s+/).every(word => name.includes(word));
                });
                return (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                    marginTop: 4, borderRadius: 8, overflow: 'hidden',
                    background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 200, overflowY: 'auto',
                  }}>
                    {/* "Myself" option for broker self-assignment */}
                    <div
                      onClick={() => { setNewAssignee(null); setAssigneeQuery(''); setShowAssigneeMenu(false); }}
                      style={{
                        padding: '9px 14px', fontSize: 13, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 8,
                        background: !newAssignee && !q ? `${theme.colors.primary}08` : 'transparent',
                        borderBottom: `1px solid ${theme.colors.border}`,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = `${theme.colors.primary}10`; }}
                      onMouseLeave={e => { e.currentTarget.style.background = !newAssignee && !q ? `${theme.colors.primary}08` : 'transparent'; }}
                    >
                      <User size={13} style={{ color: theme.colors.primary }} />
                      <span style={{ fontWeight: 600, color: theme.colors.text }}>Myself</span>
                      <span style={{ fontSize: 11, color: theme.colors.textLight }}>(default)</span>
                    </div>

                    {filtered.map(a => {
                      const agentName = `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email?.split('@')[0] || 'Agent';
                      const isSelected = newAssignee?.id === a.id;
                      return (
                        <div
                          key={a.id}
                          onClick={() => {
                            setNewAssignee({ id: a.id, name: agentName });
                            setAssigneeQuery('');
                            setShowAssigneeMenu(false);
                          }}
                          style={{
                            padding: '9px 14px', fontSize: 13, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 8,
                            background: isSelected ? `${theme.colors.primary}08` : 'transparent',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = `${theme.colors.primary}10`; }}
                          onMouseLeave={e => { e.currentTarget.style.background = isSelected ? `${theme.colors.primary}08` : 'transparent'; }}
                        >
                          <div style={{
                            width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                            background: `${theme.colors.primary}18`, color: theme.colors.primary,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 700,
                          }}>
                            {(a.first_name?.[0] || a.email?.[0] || '?').toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: theme.colors.text }}>{agentName}</div>
                            {a.email && (
                              <div style={{ fontSize: 11, color: theme.colors.textLight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {a.email}
                              </div>
                            )}
                          </div>
                          {isSelected && <CheckCircle2 size={14} style={{ color: theme.colors.primary, flexShrink: 0 }} />}
                        </div>
                      );
                    })}

                    {filtered.length === 0 && q && (
                      <div style={{ padding: '14px', fontSize: 12, color: theme.colors.textLight, textAlign: 'center' }}>
                        No agents matching "{assigneeQuery}"
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowAdd(false)} style={{
              padding: '7px 14px', borderRadius: 8, border: `1px solid ${theme.colors.border}`,
              background: 'transparent', color: theme.colors.text, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>Cancel</button>
            <button onClick={createTask} disabled={creating} style={{
              padding: '7px 14px', borderRadius: 8, border: 'none',
              background: theme.colors.primary, color: 'white', fontSize: 12, fontWeight: 600,
              cursor: creating ? 'wait' : 'pointer', opacity: creating ? 0.6 : 1,
            }}>{creating ? 'Creating…' : 'Create Task'}</button>
          </div>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, gap: 8, color: theme.colors.textLight }}>
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading tasks…
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && tasks.length === 0 && (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '48px 24px', color: theme.colors.textLight,
        }}>
          <CheckCircle2 size={40} strokeWidth={1.2} style={{ marginBottom: 12, opacity: 0.4 }} />
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
            {range === 'overdue' ? 'No overdue tasks' : range === 'today' ? 'No tasks for today' : 'No tasks found'}
          </div>
          <div style={{ fontSize: 13 }}>
            {range === 'today' ? "You're all caught up!" : 'Try a different filter or create a new task.'}
          </div>
        </div>
      )}

      {/* ── Overdue section ── */}
      {!loading && overdue.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
            fontSize: 11, fontWeight: 700, color: '#EF4444', textTransform: 'uppercase', letterSpacing: '0.04em',
          }}>
            <AlertTriangle size={13} /> Overdue ({overdue.length})
          </div>
          {overdue.map(task => (
            <TaskRow key={task.id} task={task} onToggle={toggleComplete} onOpenLead={onOpenLead} isOverdue />
          ))}
        </div>
      )}

      {/* ── Upcoming / pending section ── */}
      {!loading && upcoming.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {overdue.length > 0 && (
            <div style={{
              fontSize: 11, fontWeight: 700, color: theme.colors.textLight,
              textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8,
            }}>
              Upcoming ({upcoming.length})
            </div>
          )}
          {upcoming.map(task => (
            <TaskRow key={task.id} task={task} onToggle={toggleComplete} onOpenLead={onOpenLead} />
          ))}
        </div>
      )}

      {/* ── Completed section (collapsed) ── */}
      {!loading && completed.length > 0 && (
        <CompletedSection tasks={completed} onToggle={toggleComplete} onOpenLead={onOpenLead} />
      )}
    </div>
  );
}

// ── Task Row ──────────────────────────────────────────────────
function TaskRow({ task, onToggle, onOpenLead, isOverdue }) {
  const pm = PRIORITIES.find(p => p.id === task.priority) || PRIORITIES[1];
  const isDone = task.status === 'completed';

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '10px 14px', marginBottom: 4, borderRadius: 10,
      background: isDone ? `${theme.colors.bg}80` : theme.colors.card,
      border: `1px solid ${isOverdue ? '#FEE2E2' : theme.colors.border}`,
      transition: 'all 0.15s',
      opacity: isDone ? 0.6 : 1,
    }}
      onMouseEnter={e => { if (!isDone) e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Checkbox */}
      <button onClick={() => onToggle(task)} style={{
        background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, marginTop: 2,
        color: isDone ? theme.colors.success : theme.colors.textLight, flexShrink: 0,
      }}>
        {isDone ? <CheckCircle2 size={18} /> : <Circle size={18} />}
      </button>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: isDone ? theme.colors.textLight : theme.colors.text,
          textDecoration: isDone ? 'line-through' : 'none',
          marginBottom: 3,
        }}>
          {task.title}
        </div>
        {task.description && (
          <div style={{ fontSize: 12, color: theme.colors.textLight, marginBottom: 4, lineHeight: 1.4 }}>
            {task.description.length > 120 ? task.description.slice(0, 120) + '…' : task.description}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* Due date */}
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            fontSize: 11, color: isOverdue ? '#EF4444' : theme.colors.textLight,
            fontWeight: isOverdue ? 600 : 400,
          }}>
            <Calendar size={11} />
            {formatDate(task.due_date)}
          </span>

          {/* Priority badge */}
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
            background: `${pm.color}15`, color: pm.color,
            textTransform: 'uppercase', letterSpacing: '0.03em',
          }}>
            {pm.label}
          </span>

          {/* Source badge */}
          {task.source && task.source !== 'manual' && (
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
              background: `${theme.colors.accent}15`, color: theme.colors.accent,
            }}>
              {task.source.replace(/_/g, ' ')}
            </span>
          )}

          {/* Agent badge (visible when viewing all agents' tasks) */}
          {task.agent && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
              background: `${theme.colors.primary}12`, color: theme.colors.primary,
            }}>
              <User size={10} /> {task.agent.name}
            </span>
          )}

          {/* Linked contact */}
          {task.lead && (
            <button
              onClick={() => onOpenLead?.(task.lead.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                fontSize: 11, color: theme.colors.primary, fontWeight: 600,
                background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
              }}
            >
              <User size={11} /> {task.lead.name}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Completed Section (collapsible) ──────────────────────────
function CompletedSection({ tasks, onToggle, onOpenLead }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <button onClick={() => setExpanded(!expanded)} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px 0',
        fontSize: 11, fontWeight: 700, color: theme.colors.textLight,
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>
        <ChevronDown size={13} style={{
          transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
          transition: 'transform 0.15s',
        }} />
        Completed ({tasks.length})
      </button>
      {expanded && tasks.map(task => (
        <TaskRow key={task.id} task={task} onToggle={onToggle} onOpenLead={onOpenLead} />
      ))}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────
const formatDate = formatDateRelative;

// ── Demo Data ──────────────────────────────────────────────────
const DEMO_TASKS = [
  { id: 'demo1', title: 'Follow up with Sarah about the listing on Oak St', due_date: new Date().toISOString().split('T')[0], priority: 'high', status: 'pending', source: 'action_plan', lead: { id: 'd1', name: 'Sarah Chen' }, description: null },
  { id: 'demo2', title: 'Send disclosure packet to buyers', due_date: new Date().toISOString().split('T')[0], priority: 'medium', status: 'pending', source: 'manual', lead: { id: 'd2', name: 'Mike Johnson' }, description: 'NJ seller disclosure + lead paint' },
  { id: 'demo3', title: 'Schedule home inspection', due_date: new Date(Date.now() + 86400000).toISOString().split('T')[0], priority: 'medium', status: 'pending', source: 'deal_template', lead: { id: 'd3', name: 'Priya Sharma' }, description: null },
  { id: 'demo4', title: 'Review comp analysis', due_date: new Date(Date.now() - 86400000).toISOString().split('T')[0], priority: 'low', status: 'pending', source: 'manual', lead: null, description: null },
  { id: 'demo5', title: 'Called lender for pre-approval update', due_date: new Date(Date.now() - 172800000).toISOString().split('T')[0], priority: 'medium', status: 'completed', completed_at: new Date().toISOString(), source: 'manual', lead: { id: 'd3', name: 'Priya Sharma' }, description: null },
];
